import { workoutElapsedSeconds } from "./workoutRecovery"

// Platform-neutral, display-only projection. No set is recorded by this adapter.
export function createTrainingSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().toLowerCase()
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    return (c === "x" ? r : (r & 3) | 8).toString(16)
  })
}

const text = (value, max = 80) => String(value || "").replace(/[\r\n\t]/g, " ").trim().slice(0, max)
const number = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null

export function targetText(rawSet) {
  const settings = Array.isArray(rawSet) ? rawSet : rawSet?.settings || []
  const get = (label) => settings.find((s) => String(s?.text || "").toLowerCase() === label)
  const weight = number(get("gewicht")?.value)
  const reps = number(get("wiederholungen")?.value)
  const min = number(get("min wiederholungen")?.value)
  const max = number(get("max wiederholungen")?.value)
  const repLabel = reps !== null ? `${reps}` : min !== null && max !== null ? `${min}–${max}` : `${min ?? max ?? ""}`
  const parts = []
  if (weight !== null) parts.push(`${weight} kg`)
  if (repLabel) parts.push(`${repLabel} Wdh.`)
  for (const label of ["dauer", "distanz"]) {
    const item = get(label)
    const value = number(item?.value)
    if (value !== null) parts.push(`${value} ${text(item.unit || (label === "dauer" ? "s" : "m"), 8)}`)
  }
  return text(parts.join(" · "), 100)
}

export function buildTrainingLiveState(state, userId, now = Date.now(), { forSetEntry = false } = {}) {
  const live = state.liveSession
  if (!state.activeSplit || !live?.clientSessionId || !userId || live.userId !== String(userId)) return null
  const exercises = state.activeSplit.exercises || []
  const candidates = []
  exercises.forEach((group, index) => {
    const children = group.isSuperset ? group.exercises || [] : [group]
    children.forEach((exercise, subIndex) => {
      const done = (state.completedSetsMap[index] || []).filter((s) => !s.isWarmup && (!group.isSuperset || String(s.subExerciseId) === String(exercise._id)))
      const sets = exercise.sets || []
      candidates.push({ exercise, index, subIndex, done: done.length, sets, complete: sets.length > 0 && done.length >= sets.length })
    })
  })
  const timer = state.timerState || {}
  const selected = candidates.find((c) => c.index === state.selectedExerciseIndex && c.subIndex === (state.activeSupersetSubIndex || 0))
  const remembered = candidates.find((c) => c.index === state.liveExerciseContext?.index && c.subIndex === state.liveExerciseContext?.subIndex)
  // A persisted timer also identifies the last exercise in workouts from older app versions.
  const timerExercise = timer.isActive && candidates.find((c) => c.index === timer.exerciseIndex
    && (!timer.subExerciseId || String(c.exercise._id) === String(timer.subExerciseId)))
  const preferred = selected || remembered || timerExercise
  // Display the last exercise even when complete. Only an explicit entry action
  // may advance a stale entry link to another exercise with an unfinished set.
  const next = (preferred && (!forSetEntry || !preferred.complete) ? preferred : null) || candidates.find((c) => !c.complete)
  const sameExercise = (rest) => rest?.exerciseIndex == null || rest.exerciseIndex === next?.index
  const feedbackRest = sameExercise(state.liveFeedbackRest) ? state.liveFeedbackRest : null
  const restEndsAt = feedbackRest?.endsAt || (sameExercise(timer) && timer.isActive && timer.duration > 0 && number(timer.startTime) !== null
    ? Number(timer.startTime) + Number(timer.duration) * 1000 : null)
  const restStartedAt = feedbackRest
    ? number(feedbackRest.startedAt) : restEndsAt ? number(timer.startTime) : null
  const phase = live.phase === "ending" ? "ending" : restEndsAt > now ? "resting" : "active"
  return {
    schemaVersion: 1, clientSessionId: live.clientSessionId, userId: String(userId), owner: "phone",
    revision: live.revision || 0, updatedAt: now, phase,
    title: text(state.activeSplit.title) || "Training",
    exerciseTitle: phase === "ending" ? "" : text(next?.exercise?.title),
    exerciseInstanceId: next ? `${next.index}:${next.subIndex}` : null,
    setNumber: next?.sets.length && !next.complete ? next.done + 1 : null,
    setCount: next?.sets.length || null,
    targetText: feedbackRest ? "Rückmeldung in der App offen" : next?.complete ? "Übung abgeschlossen" : next ? targetText(next.sets[next.done]) : "",
    elapsedSeconds: workoutElapsedSeconds(state, now),
    elapsedAnchorAt: now,
    restEndsAt: phase !== "ending" ? restEndsAt : null,
    restStartedAt: phase !== "ending" && restEndsAt ? restStartedAt : null,
    syncState: live.syncState || "local",
  }
}
